import { IonButton, IonButtons } from '@ionic/react';
import { PepperTpv } from '@pepper/tpv-react';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ErrorCard from '@/components/ErrorCard/ErrorCard';
import ENVIRONMENT from '@/environments/environment';
import {
  AnalyticsOrigination, AnalyticsStepNumber, PaymentMethodAnalytics, PaymentThirdParty,
} from '@/interfaces/AnalyticsInterface/AnalyticsInterface';
import { CreditProposalResult } from '@/interfaces/CreditProposalInterface/CreditProposalInterface';
import { CustomerClassificationEnum } from '@/interfaces/CustomerInterface/CustomerInterface';
import { DecisionStatus } from '@/interfaces/DecisionInterface/DecisionInterface';
import { EDNIModalRoutes } from '@/interfaces/EDniModalInterface/EDniModalInterface';
import { EnvType } from '@/interfaces/EnviromentInterface/EnviromentInterface';
import { getDecision } from '@/redux/eDniModal/eDniModalAPI';
import { findCardType } from '@/redux/payment/existingCardHelper';
import { createPaymentCards, deletePaymentCards } from '@/redux/payment/paymentCardReducer';
import { StoredPaymentCard } from '@/redux/payment/paymentCardsStore';
import { ReusePaymentCardStatus } from '@/redux/payment/reusePaymentRequestCard';
import {
  areEqualStatuses, PaymentMethodEnum, PaymentResponse,
  PaymentSelectorEnum, PaymentState, PaymentType, PaymentWindow,
} from '@/redux/tpv/tpvPayment';
import {
  getPaymentStatus, getStoredPaymentCards, postPaymentAuthorization, postReusePaymentCard,
} from '@/redux/tpv/tpvPaymentAPI';
import {
  getTimeResult, registerLoanRefused, registerPaymentContinue, registerPaymentError, registerPaymentInfo,
} from '@/services/analytics';
import InsightService from '@/services/insights';
import TokenService from '@/services/token';
import { RootState } from '@/store';

import CardIcon from '../../../../assets/imgs/payment/creditCardIcon.svg';
import ExpressIcon from '../../../../assets/imgs/payment/expressIcon.svg';
import styles from './Payment.module.scss';

interface PaymentProps {
  nextStep: (arg0: EDNIModalRoutes) => void;
  handleLoading: (arg0: boolean) => void;
}

export default function Payment({
  handleLoading,
  nextStep,
}: PaymentProps) {
  const [methodSelected, setMethodSelected] = useState<PaymentSelectorEnum | null>(null);
  const [paymentWindow, setPaymentWindow] = useState<PaymentWindow>(
    PaymentWindow.SELECTOR,
  );
  const [paymentError, setPaymentError] = useState<boolean>(false);
  const [tpvPayment, setTpvPayment] = useState<PaymentResponse | null>(null);
  const [tpvSubtitle, setTpvSubtitle] = useState('');
  const [existingCardSelected, setExistingCardSelected] = useState<StoredPaymentCard | null>(null);
  const [retries, setRetries] = useState<number>(1);
  const customerClassification = useSelector((state: RootState) => state.customerClassification.data);
  const creditProposalResult = useSelector((state: RootState) => state.creditProposal.data);
  const paymentDone = useRef<boolean>(false);

  const dispatch = useDispatch();
  const paymentCards = useSelector((state: RootState) => state.paymentCard.data);
  const simulation = useSelector(
    (state: RootState) => state.loanSimulation.data,
  );

  const [successEnd, setSuccessEnd] = useState(false);
  const [initTime] = useState<dayjs.Dayjs>(dayjs());

  const cexToken = TokenService.getToken()!;
  const environment: EnvType = {
    type: ENVIRONMENT.ENV,
  };
  let loop: number | any;
  const maxAttempts = 40;

  // let decisionStatus: any = { signatureReady: false, isDecisionPending: true };

  // Choose initial screen. If wallet or existing cards aren't available, redirect to TPV
  const selectInitScreen = async () => {
    const storedCards = await getStoredPaymentCards_funtion(creditProposalResult!);
    if (!storedCards || (storedCards && storedCards.length === 0)) {
      createPayment();
      setPaymentWindow(PaymentWindow.TPV);
    } else {
      setPaymentWindow(PaymentWindow.SELECTOR);
    }

    handleLoading(false);
  };

  // Check if there are existing payment cards available
  const getStoredPaymentCards_funtion = async (
    creditProposal: CreditProposalResult | undefined,
  ) => {
    if (
      customerClassification?.customer_classification
      === CustomerClassificationEnum.known
      || customerClassification?.customer_classification
      === CustomerClassificationEnum.knownMiniPlus
    ) {
      const storedPaymentCards = await getStoredPaymentCards(
        creditProposal!.credit_proposal_id,
      );
      if (storedPaymentCards.payment_cards.length >= 1) {
        // si hay más de 3 tarjetas, solo muestra las 3 primeras
        const cardsDisplayed = storedPaymentCards.payment_cards.slice(0, 3);

        dispatch(createPaymentCards(cardsDisplayed));
        return cardsDisplayed;
      }

      dispatch(deletePaymentCards());
      return null;
    }

    dispatch(deletePaymentCards());
    return null;
  };

  // Checkbox handling of option selected
  const changeOptionSelected = (
    method: PaymentSelectorEnum,
    card?: StoredPaymentCard,
  ) => {
    setMethodSelected(method);
    if (card) {
      setExistingCardSelected(card);
    } else if (existingCardSelected) {
      setExistingCardSelected(null);
    }
  };

  // se crea un nuevo proceso de pago
  const createPayment = async () => {
    try {
      if (creditProposalResult?.credit_proposal_id) {
        const data = await postPaymentAuthorization(
          creditProposalResult?.credit_proposal_id,
          PaymentMethodEnum.CREDIT_CARD,
        );
        setTpvPayment(data);

        setTpvSubtitle(
          `Recuérdale a tu cliente que pagará la primera cuota de <span>${data.amount.toFixed(2).toString().replace('.', ',')
          } €</span>, a la firma del contrato`,
        );

        clearInterval(loop);
        startPaymentStatus(30000, true);
      }
    } catch (error: any) {
      if (
        error.response?.data?.result?.errors.find(
          (er: any) => er.code === 'DRF0043',
        )
        && creditProposalResult
      ) {
        getPADecision(creditProposalResult.credit_proposal_id, 0);
      } else {
        nextStep(EDNIModalRoutes.UNKNOWN_ERROR);
      }
    }
  };

  // Pooling for payment creation
  const startPaymentStatus = (
    miliseconds: number,
    isInitFeching?: boolean,
  ) => {
    let currentAttempts = 0;
    loop = setInterval(async () => {
      currentAttempts++;
      if (currentAttempts >= maxAttempts || paymentDone) {
        clearInterval(loop);
      }
      const paymentData = tpvPayment;
      if (paymentData?.payment_id && creditProposalResult) {
        const paymentStatusObject = await getPaymentStatus(
          creditProposalResult?.credit_proposal_id,
          paymentData.payment_id,
        );
        InsightService.logTrace('PaymentStatus', [
          { creditProposalId: creditProposalResult.credit_proposal_id },
          { paymentId: paymentData.payment_id },
          { paymentStatusObject: paymentStatusObject?.status },
        ]);
        if (
          (paymentStatusObject?.status.type === PaymentType.BLO
            && paymentStatusObject?.status.status === PaymentState.OK)
          || (paymentStatusObject?.status.type === PaymentType.COB
            && paymentStatusObject?.status.status === PaymentState.OK)
        ) {
          setTpvPayment(null);
          clearInterval(loop);
          paymentDone.current = true;
          getPADecision(creditProposalResult.credit_proposal_id, 0);
        }
        if (isInitFeching && currentAttempts >= 3) {
          clearInterval(loop);
          startPaymentStatus(15000);
        }
      } else {
        clearInterval(loop);
      }
    }, miliseconds);
  };

  // Payment status OK. Getting PA decision result to continue the process.
  const getPADecision = async (creditProposalId: string, counter: number) => {
    counter++;
    try {
      const decisionResp = await getDecision(
        creditProposalId,
      );
      if (decisionResp) {
        switch (decisionResp.decision) {
          case DecisionStatus.APPROVED:
          case DecisionStatus.NOT_NECESSARY:
            handleLoading(false);
            // decisionStatus = { signatureReady: true, isDecisionPending: false };
            nextStep(EDNIModalRoutes.SIGNATURE/* , { state: { decisionStatus } } */);
            break;
          case DecisionStatus.PENDING:
            // decisionStatus = { signatureReady: true, isDecisionPending: true };
            nextStep(EDNIModalRoutes.SIGNATURE/* , { state: { decisionStatus } } */);
            break;
          case DecisionStatus.DENIED:
            registerLoanRefused({ origin: AnalyticsOrigination.App, uuid: creditProposalResult?.credit_proposal_id }, 'PAC');
            nextStep(EDNIModalRoutes.DENIAL);
            break;
          case DecisionStatus.MISSING_DATA:
            nextStep(EDNIModalRoutes.UNKNOWN_ERROR);
            break;
          default:
            nextStep(EDNIModalRoutes.UNKNOWN_ERROR);
            break;
        }
        handleLoading(false);
      }
    } catch (error: any) {
      if (error.result.http_code === 409 && counter <= 12) {
        setTimeout(() => {
          getPADecision(creditProposalId, counter);
        }, 3000);
      } else {
        handleLoading(false);
        nextStep(EDNIModalRoutes.UNKNOWN_ERROR);
      }
    }
  };

  // API call to reuse payment card
  const reuseCard = async () => {
    handleLoading(true);
    try {
      const reusePayment = await postReusePaymentCard(
        creditProposalResult?.credit_proposal_id!,
        existingCardSelected?.payment_id!,
        existingCardSelected?.payment_method_id!,
      );
      switch (reusePayment?.status?.status) {
        case ReusePaymentCardStatus.OK:
          getPADecision(creditProposalResult!.credit_proposal_id, 0);
          break;
        case ReusePaymentCardStatus.KO:
          setPaymentError(true);
          handleLoading(false);
          break;
        case ReusePaymentCardStatus.PND:
          handleReusePendingStatus(reusePayment.payment_id);
          break;
        default:
          setPaymentError(true);
          handleLoading(false);
          break;
      }
    } catch (error) {
      setPaymentError(true);
      handleLoading(false);
    }
  };

  // Pooling for reuse payment card
  const handleReusePendingStatus = async (paymentId: string) => {
    const loanCreated = false;
    let currentAtempts = 0;
    const loop = setInterval(async () => {
      currentAtempts++;
      if (
        currentAtempts >= 8
      ) {
        clearInterval(loop);
      }
      const paymentStatusObject = await getPaymentStatus(
        creditProposalResult?.credit_proposal_id!,
        paymentId,
      );
      const { status } = paymentStatusObject;
      if (
        areEqualStatuses(status, {
          type: PaymentType.REC,
          status: PaymentState.OK,
        })
        && !loanCreated
      ) {
        clearInterval(loop);
        nextStep(EDNIModalRoutes.END);
      }
    }, 3 * 1000);
  };

  const handleSuccess = () => {
    if (tpvPayment) {
      const checkoutAnalytics = {
        currency: 'EUR',
        value: tpvPayment.amount || 0,
        items: {
          item_id: simulation?.commercial_offer_id || '',
          item_name: simulation?.commercial_offer_name || '',
        },
      };
      const paymentAnalytics = {
        time_elapsed: initTime ? getTimeResult(initTime) : 0,
        step_number: AnalyticsStepNumber.Payment,
        payment_type: methodSelected === PaymentSelectorEnum.EXPRESS ? PaymentMethodAnalytics.REUSED : PaymentMethodAnalytics.CARD,
        has_previous_cards: !!(paymentCards && paymentCards.length > 0),
        amount: tpvPayment?.amount || 0,
        third: tpvPayment?.third_party || '',
      };
      registerPaymentInfo({ origin: AnalyticsOrigination.App, uuid: creditProposalResult?.credit_proposal_id }, checkoutAnalytics);
      registerPaymentContinue({ origin: AnalyticsOrigination.App, uuid: creditProposalResult?.credit_proposal_id }, paymentAnalytics);
    }
    clearInterval(loop);
    setSuccessEnd(true);
    handleLoading(true);
    startPaymentStatus(3000, true);
  };

  // Handle main button click
  const handleContinue = async () => {
    handleLoading(true);
    if (existingCardSelected) {
      await reuseCard();
    } else {
      await createPayment();
      handleLoading(false);
      setPaymentWindow(PaymentWindow.TPV);
    }
  };

  // Handle payment error
  const handleTpvError = () => {
    // recibimos el número de reintentos y
    // comprobamos si sigue en rango. En ese caso haríamos el flujo habitual de crear un nuevo pago
    registerPaymentError(
      { origin: AnalyticsOrigination.App, uuid: creditProposalResult?.credit_proposal_id },
      tpvPayment?.third_party === 'SPREEDLY' ? PaymentThirdParty.SPREEDLY : PaymentThirdParty.ZRU,
    );
    if (retries < 3) {
      setRetries(retries + 1);
      handleLoading(true);
      clearInterval(loop);
      setTpvPayment(null);
      createPayment();
      setPaymentWindow(PaymentWindow.TPV);
      handleLoading(false);
    } else {
      nextStep(EDNIModalRoutes.UNKNOWN_ERROR);
    }
  };

  const handleErrorRedirection = () => {
    handleLoading(true);
    if (methodSelected === PaymentSelectorEnum.EXPRESS && paymentCards) {
      const newCardExist = paymentCards.filter(
        (paymentCard: any) => paymentCard !== existingCardSelected,
      );
      dispatch(createPaymentCards(newCardExist));
    }
    setPaymentError(false);
    setPaymentWindow(PaymentWindow.SELECTOR);
    handleLoading(false);
  };

  useEffect(() => {
    handleLoading(true);
    selectInitScreen();
  }, []);

  return (
    <div className={styles.paymentContainer}>
      {paymentError ? (
        <ErrorCard
          paymentMethod={methodSelected}
          buttonClick={() => handleErrorRedirection()}
        />
      ) : paymentWindow === PaymentWindow.SELECTOR ? (
        <>
          <div className={styles.paymentSelectorWrapper}>
            {paymentCards
              && paymentCards.length > 0
              && paymentCards.map((card: StoredPaymentCard) => (
                <div
                  className={styles.paymentSelector}
                  key={card.payment_method_id}
                  id="payment-input-existing-payment"
                  onClick={() => changeOptionSelected(PaymentSelectorEnum.EXPRESS, card)}
                >
                  <div className={styles.paymentCheckboxWrapper}>
                    <input
                      type="checkbox"
                      className={styles.paymentCheckbox}
                      checked={
                        methodSelected === PaymentSelectorEnum.EXPRESS
                        && existingCardSelected === card
                      }
                      readOnly
                    />
                    <div className={styles.paymentSelectorTitle}>
                      Paga en un click
                    </div>
                  </div>
                  <div className={styles.paymentCardTypeWrapper}>
                    {findCardType(card.iin_number)}
                    ····
                    {' '}
                    {card.last4_digits}
                  </div>
                  <img src={ExpressIcon} className={styles.paymentSelectorIcon} />
                </div>
              ))}
            <div
              className={styles.paymentSelector}
              id="payment-input-tpv"
              onClick={() => changeOptionSelected(PaymentSelectorEnum.CREDIT_CARD)}
            >
              <div className={styles.paymentCheckboxWrapper}>
                <input
                  type="checkbox"
                  className={styles.paymentCheckbox}
                  checked={methodSelected === PaymentSelectorEnum.CREDIT_CARD}
                  readOnly
                />
                <div className={styles.paymentSelectorTitle}>
                  {paymentCards && paymentCards.length > 0
                    ? 'Paga con otra tarjeta'
                    : 'Paga con tarjeta'}
                </div>
              </div>
              <img src={CardIcon} className={styles.paymentSelectorIcon} />
            </div>
          </div>

          <IonButtons className={styles.nextBtnWrapper}>
            <IonButton
              className="nextButton"
              id="end-close-button"
              disabled={!methodSelected}
              onClick={handleContinue}
            >
              Continuar
            </IonButton>
          </IonButtons>
        </>
      ) : (
        paymentWindow === PaymentWindow.TPV
        && cexToken
        && environment
        && tpvPayment
        && !successEnd && (
          <div className={styles.paymentTpvWrapper}>
            <PepperTpv
              stepEvent={() => { }}
              token={cexToken ?? ''}
              currentEnvironment={environment}
              successEnd={handleSuccess}
              error={handleTpvError}
              paymentId={tpvPayment?.payment_id}
              subtitle={tpvSubtitle}
              subtitlePosition="top"
              generalPadding="10px 0"
              saveRedirectUrls={(e: any) => {

              }}
              buttonBackgroundColor="#ff3333"
              buttonTextColor="#ffebeb"
            />
          </div>
        )
      )}
    </div>
  );
}
